import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Modal, Skeleton } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import fetchApi from '../../../../../../../lib/utils/fetch-api'
import fetch from 'isomorphic-fetch'
import { formatMoneyValue, parseMoneyValue } from './utils'
import { JsonFormChangeEvent, JsonFormSchema, PrintFormData } from './types'

interface PrintPrepareModalProps {
  id: number | string
  template: null | {
    id: number
    name: string
  }
  isModalPrintOpen: boolean
  onCancel?: (() => void) | undefined
}

export const PrintPrepareModal: React.FC<PrintPrepareModalProps> = memo(
  (props: PrintPrepareModalProps) => {
    const { id, template, isModalPrintOpen, onCancel } = props

    const [formData, setFormData] = useState<PrintFormData>({})
    const [schema, setSchema] = useState<JsonFormSchema>()
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)

    const downloadUrl = useCallback(
      async (url: string) => {
        const payload = {
          ...formData,
          total_cost: parseMoneyValue(formData?.total_cost),
          prepayment_sum: parseMoneyValue(formData?.prepayment_sum),
          payment_sum: parseMoneyValue(formData?.payment_sum),
          remains_sum: parseMoneyValue(formData?.remains_sum),
        }

        let downloadAnchorNode: HTMLAnchorElement | null = null

        try {
          const response = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            credentials: 'include',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          })

          const contentType = response.headers.get('Content-Type')
          if (contentType && contentType.includes('application/json')) {
            const content = await response.json()
            Modal.error({
              title: 'Ошибка',
              content: (
                <>
                  Не удалось получить документ, неизвестная переменная <b>{content.command}</b>
                </>
              ),
            })
            return
          }

          const blob = await response?.blob()
          if (!blob) {
            return
          }

          const disposition = response.headers.get('Content-Disposition')
          const filename = (() => {
            if (disposition && disposition.indexOf('attachment') !== -1) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition)
              if (matches != null && matches[1]) {
                return decodeURIComponent(matches[1].replace(/['"]/g, ''))
              }
            }
            return 'file'
          })()

          downloadAnchorNode = document.createElement('a')
          const urlObject = window.URL.createObjectURL(blob)
          downloadAnchorNode.setAttribute('href', urlObject)
          downloadAnchorNode.setAttribute('download', filename)
          document.body.appendChild(downloadAnchorNode)
          downloadAnchorNode.click()
          downloadAnchorNode.remove()
          window.URL.revokeObjectURL(urlObject)
          onCancel?.()
        } catch (error) {
          Modal.error({
            title: 'Ошибка',
            content: 'Не удалось скачать документ. Повторите попытку позже.',
          })
        } finally {
          if (downloadAnchorNode && downloadAnchorNode.parentNode) {
            downloadAnchorNode.parentNode.removeChild(downloadAnchorNode)
          }
        }
      },
      [formData, onCancel],
    )

    const printTemplate = useCallback(() => {
      if (!template?.id) {
        return
      }
      const url = `/api/order_print/${template.id}/${id}`
      downloadUrl(url)
    }, [downloadUrl, id, template?.id])

    useEffect(() => {
      if (!isModalPrintOpen || !template?.id) {
        return
      }

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      let isMounted = true

      const fetchData = async () => {
        setLoading(true)
        try {
          const data = await fetchApi(`/api/order_print_prepare/${template.id}/${id}`, {
            signal: controller?.signal,
          })
          const converted = convertData2JsonForm(data) as unknown as {
            resultObject: PrintFormData
            resultSchema: JsonFormSchema
          }

          const actualData = (() => {
            const newData: PrintFormData = { ...converted.resultObject }
            const totalCost = parseMoneyValue(newData?.['total_cost'])
            const prepaymentSum = parseMoneyValue(newData?.['prepayment_sum'])
            const paymentSum = parseMoneyValue(newData?.['payment_sum'])

            newData.total_cost = totalCost
            newData.prepayment_sum = prepaymentSum
            newData.payment_sum = paymentSum

            if (prepaymentSum < totalCost) {
              newData.remains_sum = Number((totalCost - prepaymentSum).toFixed(2))
            } else {
              newData.remains_sum = Math.max(
                0,
                Number((totalCost - prepaymentSum - paymentSum).toFixed(2)),
              )
            }
            return newData
          })()

          if (!isMounted) {
            return
          }

          setError(null)
          setFormData(actualData)
          setSchema(converted.resultSchema)
        } catch (error) {
          if (controller?.signal?.aborted) {
            return
          }
          setError('Не удалось загрузить данные формы. Попробуйте обновить страницу.')
        } finally {
          if (isMounted) {
            setLoading(false)
          }
        }
      }

      fetchData()

      return () => {
        isMounted = false
        controller?.abort()
      }
    }, [id, isModalPrintOpen, template?.id])

    const okHandler = useCallback(() => {
      if (!schema) {
        return
      }
      const isRequiredFields = isFormDataRequired(formData, schema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }
      printTemplate()
    }, [formData, printTemplate, schema])

    const cancelHandler = useCallback(() => {
      onCancel?.()
    }, [onCancel])

    const changeHandler = useCallback((data: JsonFormChangeEvent) => {
      setFormData(prev => {
        const next: PrintFormData = { ...prev, ...(data.formData || {}) }

        const totalCost = parseMoneyValue(next.total_cost)
        let prepaymentSum = Math.max(0, parseMoneyValue(next.prepayment_sum))
        let paymentSum = Math.max(0, parseMoneyValue(next.payment_sum))

        if (prepaymentSum + paymentSum > totalCost) {
          paymentSum = Math.max(0, totalCost - prepaymentSum)
          prepaymentSum = Math.max(0, totalCost - paymentSum)
        }

        const remainsSum = Math.max(
          0,
          Number((totalCost - (prepaymentSum + paymentSum)).toFixed(2)),
        )

        return {
          ...next,
          total_cost: totalCost,
          prepayment_sum: prepaymentSum,
          payment_sum: paymentSum,
          remains_sum: remainsSum,
        }
      })
    }, [])

    const formattedFormData = useMemo(() => {
      return {
        ...formData,
        total_cost: formatMoneyValue(formData.total_cost ?? 0),
        prepayment_sum: formatMoneyValue(formData.prepayment_sum ?? 0),
        payment_sum: formatMoneyValue(formData.payment_sum ?? 0),
        remains_sum: formatMoneyValue(formData.remains_sum ?? 0),
      }
    }, [formData])

    if (!isModalPrintOpen) {
      return null
    }
    if (!template?.id) {
      return <Alert message="Неверный ID шаблона" type="error" showIcon />
    }

    return (
      <Modal
        title={`Подготовка к печати: "${template.name}"`}
        onOk={okHandler}
        onCancel={cancelHandler}
        open={isModalPrintOpen}
        destroyOnHidden={true}
        okButtonProps={{ disabled: loading || Boolean(error) }}
        cancelButtonProps={{ disabled: loading }}
        styles={{
          body: {
            maxHeight: '70vh',
            overflow: 'auto',
          },
        }}
        maskClosable={!loading}
        closable={!loading}
      >
        {loading ? (
          <Skeleton active />
        ) : error ? (
          <Alert type="error" message={error} showIcon />
        ) : (
          <DefaultJsonForm
            formData={formattedFormData}
            schema={schema ?? {}}
            onChange={changeHandler}
            orientation="horizontal"
          />
        )}
      </Modal>
    )
  },
)

PrintPrepareModal.displayName = 'PrintPrepareModal'
